import React, { useState, useEffect } from 'react';
import { StaffLayout } from '../../components/staff/StaffLayout';
import { KitchenStaffLayout } from '../../components/staff/KitchenStaffLayout';

const KITCHEN_ROLES = ['Bar Expeditor','Bartender','Drinks Runner','Kitchen Expeditor','Chef','Food Runner','Supervisor'];

function useStaffLayout() {
  try {
    const u = JSON.parse(localStorage.getItem('userData') || '{}');
    const role = (u.roleName || '').trim().toLowerCase();
    return KITCHEN_ROLES.some(r => r.toLowerCase() === role) ? KitchenStaffLayout : StaffLayout;
  } catch { return StaffLayout; }
}
import { Armchair, RefreshCw, Bell, BellOff, CreditCard, XCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  Dialog, DialogContent, DialogDescription,
  DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const StaffSmartView = () => {
  const [seats, setSeats] = useState([]);
  const [sections, setSections] = useState([]);
  const [seatTypes, setSeatTypes] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
    checkNotifications();
    
    const timeInterval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    
    // Poll for notifications every 5 seconds
    const notificationInterval = setInterval(() => {
      checkNotifications();
    }, 5000);
    
    return () => {
      clearInterval(timeInterval);
      clearInterval(notificationInterval);
    };
  }, []);

  const checkNotifications = async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (!userData) return;
      
      const parsedUser = JSON.parse(userData);
      const staffId = parsedUser.staffId || parsedUser.id;
      
      if (!staffId) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/notifications/staff/${staffId}?unread_only=true`);
      
      if (response.data.success && response.data.notifications.length > 0) {
        // Show the first unread notification
        const firstNotification = response.data.notifications[0];
        setNotification(firstNotification);
        setNotificationOpen(true);
      }
    } catch (error) {
      console.error('Error checking notifications:', error);
    }
  };

  const handleNotificationAcknowledge = async () => {
    try {
      if (notification) {
        await axios.put(`${BACKEND_URL}/api/notifications/${notification.id}/read`);
      }
      setNotificationOpen(false);
      setNotification(null);
      // Refresh the page data
      fetchData();
    } catch (error) {
      console.error('Error acknowledging notification:', error);
      setNotificationOpen(false);
      setNotification(null);
    }
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const userData = localStorage.getItem('userData');
      if (!userData) return;

      const parsedUser = JSON.parse(userData);
      const propertyId = parsedUser.entityId;
      const selectedSectionId = parsedUser.selectedSectionId; // Get selected section from staff data

      const [seatsRes, groupsRes, typesRes, allocsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/seats/${propertyId}`),
        axios.get(`${BACKEND_URL}/api/sections/${propertyId}`),
        axios.get(`${BACKEND_URL}/api/seat-types/${propertyId}`),
        axios.get(`${BACKEND_URL}/api/allocations/${propertyId}`)
      ]);

      // Filter seats to only show those from the selected section
      if (seatsRes.data.success) {
        const allSeats = seatsRes.data.seats;
        if (selectedSectionId) {
          // Staff has selected a section - show only that section's seats
          const filteredSeats = allSeats.filter(seat => seat.sectionId === selectedSectionId);
          setSeats(filteredSeats);
        } else {
          // No section selected (e.g., Pool and Beach Manager) - show all seats
          setSeats(allSeats);
        }
      }
      
      // Filter sections to only show the selected section
      if (groupsRes.data.success) {
        const allSections = groupsRes.data.sections;
        if (selectedSectionId) {
          // Show only the selected section
          const filteredSections = allSections.filter(section => section.id === selectedSectionId);
          setSections(filteredSections);
        } else {
          // Show all sections
          setSections(allSections);
        }
      }
      
      if (typesRes.data.success) setSeatTypes(typesRes.data.seatTypes);
      if (allocsRes.data.success) setAllocations(allocsRes.data.allocations);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSeatType = (seatTypeId) => {
    return seatTypes.find(type => type.id === seatTypeId) || { name: 'Standard', icon: null };
  };

  /* ── Stop calling / checkout ── */
  const [stoppingId, setStoppingId] = useState(null);

  const stopCalling = async (e, allocationId, callingFlag) => {
    e.stopPropagation();
    setStoppingId(allocationId);
    try {
      const mode = callingFlag === 'Calling for Checkout' ? 'Calling for Checkout' : 'Calling';
      await axios.post(`${BACKEND_URL}/api/allocations/${allocationId}/toggle-calling`, { callingMode: mode });
      // Optimistically clear in local state
      setAllocations(prev => prev.map(a =>
        a.id === allocationId ? { ...a, callingFlag: 'Non Calling' } : a
      ));
    } catch (err) {
      console.error('Error stopping calling:', err);
    } finally {
      setStoppingId(null);
    }
  };

  const getSeatStatus = (seatId) => {
    const allocation = allocations.find(alloc => 
      alloc.seatIds.includes(seatId) && alloc.status !== 'Complete'
    );
    
    if (!allocation) {
      return { status: 'Free', color: 'bg-white border-slate-300', allocation: null, isCalling: false, callingDuration: 0 };
    }
    
    const isCalling = allocation.callingFlag === "Calling" || allocation.callingFlag === "Calling for Checkout";
    
    let callingDuration = 0;
    if (isCalling && allocation.updatedAt) {
      const updatedAt = new Date(allocation.updatedAt);
      callingDuration = Math.floor((currentTime - updatedAt.getTime()) / 1000);
    }
    
    let color;
    if (isCalling) {
      if (callingDuration > 90) {
        color = 'bg-red-500 border-red-600';
      } else if (callingDuration > 45) {
        color = 'bg-orange-500 border-orange-600';
      } else if (callingDuration > 15) {
        color = 'bg-yellow-500 border-yellow-600';
      } else {
        color = 'bg-blue-400 border-blue-500';
      }
    } else {
      const statusMap = {
        'Allocated': 'bg-blue-500 border-blue-600',
        'Active': 'bg-green-500 border-green-600',
        'Billing': 'bg-purple-500 border-purple-600',
        'Clear': 'bg-teal-500 border-teal-600',
        'Complete': 'bg-white border-slate-300'
      };
      color = statusMap[allocation.status] || 'bg-white border-slate-300';
    }
    
    return { 
      status: allocation.status,
      color: color,
      allocation: allocation,
      isCalling: isCalling,
      callingDuration: callingDuration
    };
  };

  const groupedSeats = sections.map(section => {
    const groupSeats = seats.filter(seat => seat.sectionId === section.id);
    return {
      ...section,
      seats: groupSeats
    };
  });

  const ungroupedSeats = seats.filter(seat => !seat.sectionId);

  const totalSeats = seats.length;
  const occupiedSeats = allocations.filter(a => a.status !== 'Complete').length;
  const availableSeats = totalSeats - occupiedSeats;

  const Layout = useStaffLayout();

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading SmartView...</p>
          </div>
        </div>
      </Layout>
    );
  }

  // Get selected section info
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const selectedSectionName = userData.selectedSectionName;

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">SmartView Dashboard</h1>
            <p className="text-slate-600 mt-1">
              {selectedSectionName ? `Viewing: ${selectedSectionName}` : 'Real-time seat allocation overview'}
            </p>
          </div>
          <div className="flex items-center space-x-3">
            {selectedSectionName && (
              <Button 
                onClick={() => navigate('/staff/section-selection', { state: { staffData: userData } })}
                variant="outline"
                className="border-blue-500 text-blue-600 hover:bg-blue-50"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Switch Section
              </Button>
            )}
            <Button onClick={fetchData} variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Total Seats</p>
                <p className="text-3xl font-bold text-slate-900">{totalSeats}</p>
              </div>
              <Armchair className="w-12 h-12 text-blue-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Available</p>
                <p className="text-3xl font-bold text-green-600">{availableSeats}</p>
              </div>
              <Armchair className="w-12 h-12 text-green-500" />
            </div>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Occupied</p>
                <p className="text-3xl font-bold text-red-600">{occupiedSeats}</p>
              </div>
              <Armchair className="w-12 h-12 text-red-500" />
            </div>
          </div>
        </div>

        {/* Sectioned Seats */}
        {groupedSeats.map(section => (
          section.seats.length > 0 && (
            <div key={section.id} className="bg-white rounded-xl shadow-md overflow-visible">
              <div className="bg-gradient-to-r from-slate-700 to-slate-800 px-6 py-4">
                <h2 className="text-xl font-bold text-white">{section.name}</h2>
                <p className="text-slate-300 text-sm mt-1">{section.seats.length} seats</p>
              </div>
              <div className="p-6">
                {(() => {
                  // Section seats by allocation
                  const seatsWithAllocation = [];
                  const seatsWithoutAllocation = [];
                  
                  section.seats.forEach(seat => {
                    const { allocation } = getSeatStatus(seat.id);
                    if (allocation) {
                      seatsWithAllocation.push({ seat, allocation });
                    } else {
                      seatsWithoutAllocation.push(seat);
                    }
                  });
                  
                  // Section by allocation ID
                  const allocationSections = {};
                  seatsWithAllocation.forEach(({ seat, allocation }) => {
                    if (!allocationSections[allocation.id]) {
                      allocationSections[allocation.id] = {
                        allocation,
                        seats: []
                      };
                    }
                    allocationSections[allocation.id].seats.push(seat);
                  });
                  
                  return (
                    <div className="flex flex-wrap gap-y-2 gap-x-4">
                      {/* Allocated seats grouped by allocation */}
                      {Object.values(allocationSections).map(({ allocation, seats: allocSeats }) => {
                        const isCalling = allocation.callingFlag === 'Calling';
                        const isCheckout = allocation.callingFlag === 'Calling for Checkout';
                        const isActive = isCalling || isCheckout;
                        return (
                        <div 
                          key={allocation.id}
                          className={`border-2 border-dashed rounded-lg p-2 cursor-pointer transition-colors inline-flex flex-col gap-2 relative section ${
                            isCalling ? 'border-amber-400 bg-amber-50/50 hover:bg-amber-50' :
                            isCheckout ? 'border-emerald-400 bg-emerald-50/50 hover:bg-emerald-50' :
                            'border-blue-400 bg-blue-50/30 hover:bg-blue-50/50'
                          }`}
                          onClick={() => navigate(`/user/allocation/${allocation.id}`)}
                        >
                          <div className="flex items-center space-x-2 px-1">
                            <div className={`w-1.5 h-1.5 rounded-full ${isActive ? 'animate-pulse' : ''} ${isCalling ? 'bg-amber-500' : isCheckout ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>
                            <span className="text-xs font-semibold text-slate-700">
                              {allocation.guestName} - Room {allocation.roomNumber}
                            </span>
                            {allocation.guestCategory && (
                              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                {allocation.guestCategory}
                              </span>
                            )}
                            {/* Calling badge */}
                            {isCalling && (
                              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                <Bell className="w-2.5 h-2.5" /> Calling
                              </span>
                            )}
                            {isCheckout && (
                              <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                                <CreditCard className="w-2.5 h-2.5" /> Checkout
                              </span>
                            )}
                          </div>
                          
                          <div className="flex flex-wrap gap-y-2 gap-x-4">
                            {allocSeats.map(seat => {
                              const { status, color, isCalling: seatCalling } = getSeatStatus(seat.id);
                              const seatType = getSeatType(seat.seatTypeId);
                              
                              return (
                                <div key={seat.id} className="relative">
                                  <div
                                    className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg cursor-pointer flex flex-col items-center justify-center h-[88px] w-[60px] ${
                                      seatCalling ? 'animate-pulse ring-4 ring-red-500 ring-offset-2 shadow-xl shadow-red-500/50' : ''
                                    }`}
                                  >
                                    {seatType.icon ? (
                                      <img 
                                        src={seatType.icon} 
                                        alt={seatType.name}
                                        className="w-6 h-6 object-contain mb-1 brightness-0 invert"
                                      />
                                    ) : (
                                      <Armchair className="w-5 h-5 mb-1 text-white" />
                                    )}
                                    <span className="text-[11px] font-semibold text-white">
                                      {seat.seatNumber}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>

                          {/* Stop Calling / Stop Checkout button */}
                          {isActive && (
                            <button
                              onClick={(e) => stopCalling(e, allocation.id, allocation.callingFlag)}
                              disabled={stoppingId === allocation.id}
                              className={`flex items-center justify-center gap-1.5 w-full px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 disabled:opacity-60 ${
                                isCalling
                                  ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                  : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                              }`}
                              data-testid={`stop-calling-${allocation.id}`}
                            >
                              {stoppingId === allocation.id ? (
                                <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                              ) : isCalling ? (
                                <><BellOff className="w-3 h-3" /> Stop Calling</>
                              ) : (
                                <><XCircle className="w-3 h-3" /> Stop Checkout</>
                              )}
                            </button>
                          )}
                          
                          {/* Allocation Hover Tooltip */}
                          <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                            <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[200px]">
                              <p className="font-bold text-sm mb-1">{allocation.guestName}</p>
                              <p className="text-slate-300 mb-2">Room {allocation.roomNumber}</p>
                              <div className="border-t border-slate-700 pt-2 space-y-1">
                                <p className="font-semibold text-teal-400">Status: {allocation.status}</p>
                                {allocation.guestCategory && (
                                  <p className="text-amber-400">
                                    <span className="text-slate-400">Category:</span> {allocation.guestCategory}
                                  </p>
                                )}
                                <p className="text-slate-200 text-[10px] mt-1">
                                  <span className="text-slate-400">Seats:</span> {allocSeats.map(s => s.seatNumber).join(', ')}
                                </p>
                              </div>
                            </div>
                            <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                        );
                      })}
                      
                      {/* Free seats (not allocated) */}
                      {seatsWithoutAllocation.map(seat => {
                        const { status, color } = getSeatStatus(seat.id);
                        const seatType = getSeatType(seat.seatTypeId);
                        
                        return (
                          <div key={seat.id} className="group relative">
                            <div
                              className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg cursor-default flex flex-col items-center justify-center h-[88px] w-[60px]`}
                            >
                              {seatType.icon ? (
                                <img 
                                  src={seatType.icon} 
                                  alt={seatType.name}
                                  className="w-6 h-6 object-contain mb-1"
                                />
                              ) : (
                                <Armchair className="w-5 h-5 mb-1 text-slate-400" />
                              )}
                              <span className="text-[11px] font-semibold text-slate-700">
                                {seat.seatNumber}
                              </span>
                            </div>
                            
                            {/* Tooltip */}
                            <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                              <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[180px]">
                                <p className="font-bold text-sm mb-1">{seat.seatNumber}</p>
                                <p className="text-slate-300 mb-2">{seatType.name}</p>
                                <div className="border-t border-slate-700 pt-2">
                                  <p className="text-slate-300 italic">Available</p>
                                </div>
                              </div>
                              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          )
        ))}

        {/* Ungrouped Seats */}
        {ungroupedSeats.length > 0 && (
          <div className="bg-white rounded-xl shadow-md overflow-visible">
            <div className="bg-gradient-to-r from-slate-500 to-slate-600 px-6 py-4">
              <h2 className="text-xl font-bold text-white">Ungrouped Seats</h2>
              <p className="text-slate-300 text-sm mt-1">{ungroupedSeats.length} seats</p>
            </div>
            <div className="p-6">
              {(() => {
                // Section ungrouped seats by allocation
                const seatsWithAllocation = [];
                const seatsWithoutAllocation = [];
                
                ungroupedSeats.forEach(seat => {
                  const { allocation } = getSeatStatus(seat.id);
                  if (allocation) {
                    seatsWithAllocation.push({ seat, allocation });
                  } else {
                    seatsWithoutAllocation.push(seat);
                  }
                });
                
                // Section by allocation ID
                const allocationSections = {};
                seatsWithAllocation.forEach(({ seat, allocation }) => {
                  if (!allocationSections[allocation.id]) {
                    allocationSections[allocation.id] = {
                      allocation,
                      seats: []
                    };
                  }
                  allocationSections[allocation.id].seats.push(seat);
                });
                
                return (
                  <div className="flex flex-wrap gap-y-2 gap-x-4">
                    {/* Allocated seats grouped by allocation */}
                    {Object.values(allocationSections).map(({ allocation, seats: allocSeats }) => {
                      const isCalling = allocation.callingFlag === 'Calling';
                      const isCheckout = allocation.callingFlag === 'Calling for Checkout';
                      const isActive = isCalling || isCheckout;
                      return (
                      <div 
                        key={allocation.id}
                        className={`border-2 border-dashed rounded-lg p-2 cursor-pointer transition-colors inline-flex flex-col gap-2 relative section ${
                          isCalling ? 'border-amber-400 bg-amber-50/50 hover:bg-amber-50' :
                          isCheckout ? 'border-emerald-400 bg-emerald-50/50 hover:bg-emerald-50' :
                          'border-blue-400 bg-blue-50/30 hover:bg-blue-50/50'
                        }`}
                        onClick={() => navigate(`/user/allocation/${allocation.id}`)}
                      >
                        <div className="flex items-center space-x-2 px-1">
                          <div className={`w-1.5 h-1.5 rounded-full ${isActive ? 'animate-pulse' : ''} ${isCalling ? 'bg-amber-500' : isCheckout ? 'bg-emerald-500' : 'bg-blue-500'}`}></div>
                          <span className="text-xs font-semibold text-slate-700">
                            {allocation.guestName} - Room {allocation.roomNumber}
                          </span>
                          {allocation.guestCategory && (
                            <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                              {allocation.guestCategory}
                            </span>
                          )}
                          {isCalling && (
                            <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                              <Bell className="w-2.5 h-2.5" /> Calling
                            </span>
                          )}
                          {isCheckout && (
                            <span className="bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center gap-1">
                              <CreditCard className="w-2.5 h-2.5" /> Checkout
                            </span>
                          )}
                        </div>
                        
                        <div className="flex flex-wrap gap-y-2 gap-x-4">
                          {allocSeats.map(seat => {
                            const { status, color, isCalling: seatCalling } = getSeatStatus(seat.id);
                            const seatType = getSeatType(seat.seatTypeId);
                            
                            return (
                              <div
                                key={seat.id}
                                className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg cursor-pointer flex flex-col items-center justify-center h-[88px] w-[60px] ${
                                  seatCalling ? 'animate-pulse ring-4 ring-red-500 ring-offset-2 shadow-xl shadow-red-500/50' : ''
                                }`}
                              >
                                {seatType.icon ? (
                                  <img 
                                    src={seatType.icon} 
                                    alt={seatType.name}
                                    className="w-6 h-6 object-contain mb-1 brightness-0 invert"
                                  />
                                ) : (
                                  <Armchair className="w-5 h-5 mb-1 text-white" />
                                )}
                                <span className="text-[11px] font-semibold text-white">
                                  {seat.seatNumber}
                                </span>
                              </div>
                            );
                          })}
                        </div>

                        {/* Stop Calling / Stop Checkout button */}
                        {isActive && (
                          <button
                            onClick={(e) => stopCalling(e, allocation.id, allocation.callingFlag)}
                            disabled={stoppingId === allocation.id}
                            className={`flex items-center justify-center gap-1.5 w-full px-3 py-1.5 rounded-lg text-xs font-bold transition-all active:scale-95 disabled:opacity-60 ${
                              isCalling
                                ? 'bg-amber-500 hover:bg-amber-600 text-white'
                                : 'bg-emerald-500 hover:bg-emerald-600 text-white'
                            }`}
                            data-testid={`stop-calling-${allocation.id}`}
                          >
                            {stoppingId === allocation.id ? (
                              <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                            ) : isCalling ? (
                              <><BellOff className="w-3 h-3" /> Stop Calling</>
                            ) : (
                              <><XCircle className="w-3 h-3" /> Stop Checkout</>
                            )}
                          </button>
                        )}
                        
                        {/* Allocation Hover Tooltip */}
                        <div className="absolute bottom-full left-0 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                          <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[200px]">
                            <p className="font-bold text-sm mb-1">{allocation.guestName}</p>
                            <p className="text-slate-300 mb-2">Room {allocation.roomNumber}</p>
                            <div className="border-t border-slate-700 pt-2 space-y-1">
                              <p className="font-semibold text-teal-400">Status: {allocation.status}</p>
                              {allocation.guestCategory && (
                                <p className="text-amber-400">
                                  <span className="text-slate-400">Category:</span> {allocation.guestCategory}
                                </p>
                              )}
                              <p className="text-slate-200 text-[10px] mt-1">
                                <span className="text-slate-400">Seats:</span> {allocSeats.map(s => s.seatNumber).join(', ')}
                              </p>
                            </div>
                          </div>
                          <div className="absolute top-full left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                        </div>
                      </div>
                      );
                    })}
                    
                    {/* Free seats (not allocated) */}
                    {seatsWithoutAllocation.map(seat => {
                      const { status, color } = getSeatStatus(seat.id);
                      const seatType = getSeatType(seat.seatTypeId);
                      
                      return (
                        <div key={seat.id} className="group relative">
                          <div
                            className={`${color} rounded-lg border-2 p-2 transition-all hover:scale-105 hover:shadow-lg cursor-default flex flex-col items-center justify-center h-[88px] w-[60px]`}
                          >
                            {seatType.icon ? (
                              <img 
                                src={seatType.icon} 
                                alt={seatType.name}
                                className="w-6 h-6 object-contain mb-1"
                              />
                            ) : (
                              <Armchair className="w-5 h-5 mb-1 text-slate-400" />
                            )}
                            <span className="text-[11px] font-semibold text-slate-700">
                              {seat.seatNumber}
                            </span>
                          </div>
                          
                          {/* Tooltip */}
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-[100]">
                            <div className="bg-slate-900 text-white text-xs rounded-lg px-3 py-2 shadow-xl min-w-[180px]">
                              <p className="font-bold text-sm mb-1">{seat.seatNumber}</p>
                              <p className="text-slate-300 mb-2">{seatType.name}</p>
                              <div className="border-t border-slate-700 pt-2">
                                <p className="text-slate-300 italic">Available</p>
                              </div>
                            </div>
                            <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-slate-900"></div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Notification Modal */}
      <Dialog open={notificationOpen} onOpenChange={() => {}}>
        <DialogContent className="sm:max-w-[425px]" onPointerDownOutside={(e) => e.preventDefault()}>
          <DialogHeader>
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-amber-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <DialogTitle>{notification?.title || 'Notification'}</DialogTitle>
                <DialogDescription>
                  Seat Change Alert
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-slate-700">{notification?.message}</p>
            
            {notification?.data && (
              <div className="mt-4 bg-slate-50 rounded-lg p-3 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Guest:</span>
                  <span className="font-medium text-slate-700">{notification.data.guestName}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Room:</span>
                  <span className="font-medium text-slate-700">{notification.data.roomNumber}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Previous Seats:</span>
                  <span className="font-medium text-red-600">{notification.data.oldSeats?.join(', ')}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">New Seats:</span>
                  <span className="font-medium text-green-600">{notification.data.newSeats?.join(', ')}</span>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button 
              onClick={handleNotificationAcknowledge}
              className="w-full bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              data-testid="notification-ok-btn"
            >
              Okay
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};
